// Admin editor for the shared Data Trust pillar weights.

import React, { useEffect, useMemo, useState } from "react";
import { T } from "../../app/tokens.js";
import { getDataTrustWeights, putDataTrustWeights, getDataTrustThreshold, putDataTrustThreshold } from "../../data/commandIndexClient.js";

const PILLAR_ORDER = ["headcount", "leases", "badging", "seats", "projects", "open_roles"];
const FALLBACKS = {
  headcount: { label: "Headcount", source: "Workday", weight: 25 },
  leases: { label: "Leases", source: "Spacebase", weight: 25 },
  badging: { label: "Badging / attendance", source: "Alta/Verkada", weight: 20 },
  seats: { label: "Seats / capacity", source: "Internal capacity schedule", weight: 15 },
  projects: { label: "Projects", source: "Slack + docs", weight: 10, isActive: false },
  open_roles: { label: "Open roles", source: "Greenhouse", weight: 5 },
};

const BUTTON = {
  border: "none",
  borderRadius: 6,
  padding: "8px 14px",
  fontSize: T.fsSm,
  fontWeight: T.fwBold,
  cursor: "pointer",
};

function orderedPillars(rows) {
  const byKey = new Map((Array.isArray(rows) ? rows : []).map((row) => [row.key ?? row.pillarKey ?? row.pillar_key, row]));
  return PILLAR_ORDER.map((key) => ({ key, ...FALLBACKS[key], ...(byKey.get(key) ?? {}) }));
}


export function DataTrustWeightsPanel({ currentRole } = {}) {
  const isAdmin = currentRole === "admin";
  const [pillars, setPillars] = useState(() => orderedPillars([]));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [threshold, setThreshold] = useState(7);
  const [thresholdSaving, setThresholdSaving] = useState(false);
  const [thresholdSaved, setThresholdSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getDataTrustWeights()
      .then((result) => {
        if (!cancelled) setPillars(orderedPillars(result?.pillars));
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Could not load Data Trust weights.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getDataTrustThreshold()
      .then(({ threshold: value }) => { if (!cancelled) setThreshold(value); })
      .catch((err) => { if (!cancelled) setError(err?.message || "Could not load confidence threshold."); });
    return () => { cancelled = true; };
  }, []);

  async function saveThreshold() {
    const value = Number(threshold);
    if (!Number.isInteger(value) || value < 0 || value > 10 || threshold === "") {
      setError("Choose a whole-number threshold from 0 to 10.");
      return;
    }
    setThresholdSaving(true);
    setThresholdSaved(false);
    setError(null);
    try {
      const result = await putDataTrustThreshold(value);
      setThreshold(result.threshold);
      setThresholdSaved(true);
    } catch (err) {
      setError(err?.message || "Could not save confidence threshold.");
    } finally {
      setThresholdSaving(false);
    }
  }

  const totalConfigured = useMemo(
    () => pillars.reduce((sum, pillar) => sum + (Number(pillar.weight) || 0), 0),
    [pillars],
  );
  const totalActive = useMemo(
    () => pillars.reduce((sum, pillar) => sum + (pillar.isActive !== false ? Number(pillar.weight) || 0 : 0), 0),
    [pillars],
  );

  const updatePillar = (key, patch) => {
    setSaved(false);
    setPillars((current) => current.map((pillar) => pillar.key === key ? { ...pillar, ...patch } : pillar));
  };

  const handleSave = async () => {
    setError(null);
    setSaved(false);
    const payload = pillars.map((pillar) => ({ key: pillar.key, weight: Number(pillar.weight), isActive: pillar.isActive !== false }));
    if (payload.some((pillar) => !Number.isFinite(pillar.weight) || pillar.weight < 0)) {
      setError("Weights must be non-negative numbers.");
      return;
    }
    if (totalActive <= 0) {
      setError("At least one active pillar must have a positive weight.");
      return;
    }
    setSaving(true);
    try {
      const result = await putDataTrustWeights(payload);
      setPillars(orderedPillars(result?.pillars));
      setSaved(true);
    } catch (err) {
      setError(err?.message || "Could not save Data Trust weights.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="data-trust-weights-panel">
      <h1 style={{ margin: 0, color: T.dark, fontSize: T.fsLg5 }}>Data Trust weights</h1>
      <p style={{ color: T.mid, lineHeight: 1.5, margin: "6px 0 18px" }}>
        Set the shared contribution of each source-backed pillar. Excluded pillars contribute zero;
        active scored pillars are normalized to the full composite.
      </p>

      {!isAdmin && (
        <div data-testid="data-trust-weights-read-only" style={{ background: T.lightGray, color: T.mid, padding: "10px 12px", borderRadius: 6, marginBottom: 14 }}>
          Read-only — only admins can edit Data Trust weights.
        </div>
      )}
      {error && <div data-testid="data-trust-weights-error" role="alert" style={{ color: T.red, marginBottom: 12 }}>{error}</div>}
      {saved && <div data-testid="data-trust-weights-saved" style={{ color: T.green, marginBottom: 12 }}>Data Trust weights saved.</div>}

      {loading ? (
        <div data-testid="data-trust-weights-loading" style={{ color: T.mid }}>Loading weights…</div>
      ) : (
        <>
          <div style={{ border: `1px solid ${T.border}`, borderRadius: 7, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 130px 150px", gap: 12, padding: "9px 12px", background: T.lightGray, color: T.mid, fontSize: T.fsXxs, fontWeight: T.fwBold, textTransform: "uppercase" }}>
              <span>Pillar</span><span>Configured</span><span>Composite treatment</span>
            </div>
            {pillars.map((pillar) => {
              const effective = pillar.isActive !== false && totalActive > 0
                ? (Number(pillar.weight) || 0) / totalActive * 100
                : 0;
              const projects = pillar.key === "projects";
              return (
                <div key={pillar.key} data-testid={`data-trust-weight-row-${pillar.key}`} style={{ display: "grid", gridTemplateColumns: "1fr 130px 150px", gap: 12, alignItems: "center", padding: "11px 12px", borderTop: `1px solid ${T.lightGray}` }}>
                  <div>
                    <div style={{ color: T.dark, fontWeight: T.fwSemibold }}>{pillar.label}</div>
                    <div style={{ color: T.mid, fontSize: T.fsXs }}>{pillar.source}</div>
                  </div>
                  <label>
                    <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden" }}>Configured weight for {pillar.label}</span>
                    <input
                      data-testid={`data-trust-weight-${pillar.key}`}
                      type="number"
                      min="0"
                      step="0.1"
                      value={pillar.weight}
                      disabled={!isAdmin}
                      onChange={(event) => updatePillar(pillar.key, { weight: event.target.value })}
                      style={{ width: 88, padding: "6px 7px", border: `1px solid ${T.border}`, borderRadius: 4, color: T.dark, background: isAdmin ? T.white : T.lightGray }}
                    /> %
                  </label>
                  <label style={{ color: T.mid, fontSize: T.fsSm }}>
                    <input
                      data-testid={`data-trust-exclude-${pillar.key}`}
                      type="checkbox"
                      checked={pillar.isActive === false}
                      disabled={!isAdmin || projects}
                      onChange={(event) => updatePillar(pillar.key, { isActive: !event.target.checked })}
                    />{" "}
                    {projects ? "Excluded until wired" : pillar.isActive === false ? "Excluded" : `${Math.round(effective * 10) / 10}% effective`}
                  </label>
                </div>
              );
            })}
          </div>
          <div data-testid="data-trust-weight-summary" style={{ display: "flex", gap: 18, marginTop: 14, color: T.mid, fontSize: T.fsSm }}>
            <span>Configured total: <strong>{Math.round(totalConfigured * 10) / 10}%</strong></span>
            <span>Active normalized total: <strong>{totalActive > 0 ? "100%" : "0%"}</strong></span>
          </div>
          {isAdmin && (
            <button type="button" data-testid="data-trust-weights-save" onClick={handleSave} disabled={saving} style={{ ...BUTTON, background: T.blue, color: T.white, marginTop: 18 }}>
              {saving ? "Saving…" : "Save weights"}
            </button>
          )}
          <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 22, paddingTop: 16 }}>
            <h2 style={{ color: T.dark, fontSize: T.fsMd }}>Source confidence action threshold</h2>
            <p style={{ color: T.mid }}>Scorable sources rated at or below this value can appear as validation opportunities when restoring confidence would recover points.</p>
            <label style={{ color: T.dark }}>
              Confidence threshold (0-10)
              <input type="number" min="0" max="10" step="1" aria-label="Confidence action threshold"
                value={threshold} disabled={!isAdmin || thresholdSaving}
                onChange={(event) => { setThreshold(event.target.value); setThresholdSaved(false); }}
                style={{ display: "block", width: 80, padding: 7, border: `1px solid ${T.border}`, borderRadius: 6 }} />
            </label>
            {isAdmin && <button type="button" onClick={saveThreshold} disabled={thresholdSaving}
              style={{ ...BUTTON, background: T.blue, color: T.white, marginTop: 10 }}>
              {thresholdSaving ? "Saving..." : "Save threshold"}
            </button>}
            {thresholdSaved && <span role="status" style={{ marginLeft: 10, color: T.green }}>Threshold saved.</span>}
          </div>
        </>
      )}
    </section>
  );
}